//frontend/src/components/dashboard/ProductsImport.js
import React, { useState } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Button,
   Box,
   Alert,
   LinearProgress,
   Typography,
   TextField,
   Stepper,
   Step,
   StepLabel,
   Grid,
   Paper
} from '@mui/material';
import { read, utils } from 'xlsx';
import axiosInstance from '../../utils/axios';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';


export const ProductsImport = ({ open, onClose, onImportComplete }) => {
   const { t } = useTranslation();
   const [activeStep, setActiveStep] = useState(0);
   const [error, setError] = useState(null);
   const [progress, setProgress] = useState(0);
   const [excelData, setExcelData] = useState(null);
   const [columnMappings, setColumnMappings] = useState({
       name: 'A1',
       category: 'B1',
       price: 'C1',
       costPrice: 'D1',
       warehouseName: 'E1',
       warehouseAddress: 'F1',
       articleNumber: 'G1',
       unit: 'H1',
       weight: 'I1',
       length: 'J1',
       width: 'K1',
       height: 'L1',
       stock: 'M1'
   });

   const renderInstructions = () => (
    <Paper sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <InfoIcon color="info" />
        <Typography variant="body2">
            {t('products.import.guide')}
        </Typography>
    </Paper>
 );

   const steps = [
       t('products.import.steps.upload'),
       t('products.import.steps.map'),
       t('products.import.steps.import')
   ];

   const getColumnIndices = (a1Notation) => {
       const col = a1Notation.match(/[A-Z]+/)[0];
       const row = parseInt(a1Notation.match(/\d+/)[0]) - 1;
       
       let columnIndex = 0;
       for (let i = 0; i < col.length; i++) {
           columnIndex = columnIndex * 26 + col.charCodeAt(i) - 'A'.charCodeAt(0);
       }
       
       return { columnIndex, row };
   };

   const handleFileUpload = async (e) => {
       const file = e.target.files[0];
       if (!file) return;

       try {
           const data = await file.arrayBuffer();
           const workbook = read(data);
           const worksheet = workbook.Sheets[workbook.SheetNames[0]];
           const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
           
           setExcelData(jsonData);
           setActiveStep(1);
       } catch (error) {
           setError(t('products.import.errors.fileRead'));
       }
   };

   const handleColumnMapping = (field, value) => {
       setColumnMappings(prev => ({
           ...prev,
           [field]: value.toUpperCase()
       }));
   };

   const handleImport = async () => {
    try {
        setActiveStep(2);
        setProgress(25);

        const products = excelData.slice(1)
            .map(row => {
                const product = {};
                Object.entries(columnMappings).forEach(([field, a1Notation]) => {
                    try {
                        const { columnIndex } = getColumnIndices(a1Notation);
                        if (row[columnIndex] !== undefined) {
                            product[field] = row[columnIndex];
                        }
                    } catch (e) {
                        console.error(`Error mapping column ${field}:`, e);
                    }
                });
                return product;
            })
            .filter(product => product.name); // Фильтруем только продукты с именем

        if (products.length === 0) {
            throw new Error('No valid products found in file');
        }

        setProgress(50);
        await axiosInstance.post('/products/import', { products });
        setProgress(100);
        onImportComplete();
        onClose();
    } catch (error) {
        setError(error.message || t('products.import.errors.importFailed'));
        setProgress(0);
    }
};

const renderStep = () => {
    switch (activeStep) {
        case 0:
            return (
                <Box sx={{ textAlign: 'center' }}>
                    <Paper sx={{ p: 3, mb: 4, display: 'flex', alignItems: 'flex-start', gap: 2, textAlign: 'left' }}>
                        <InfoIcon color="info" sx={{ mt: 0.5 }} />
                        <Box>
                            <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                                {t('products.import.guideTitle')}
                            </Typography>
                            <Typography variant="body2" component="div">
                                {t('products.import.guide')}
                            </Typography>
                        </Box>
                    </Paper>
 
                    <Button
                        variant="contained"
                        component="label"
                        size="large"
                    >
                        {t('products.import.selectfile')}
                        <input
                            hidden
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleFileUpload}
                        />
                    </Button>
                </Box>
            );
 
        case 1:
            return (
                <Box sx={{ mt: 2 }}>
                    <Paper sx={{ p: 3, mb: 3, backgroundColor: 'info.lighter' }}>
                        <Typography variant="subtitle2" gutterBottom color="info.main">
                            {t('products.import.instructions')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('products.import.exampleFormat')}
                        </Typography>
                    </Paper>
                    <Grid container spacing={2}>
                        {Object.entries(columnMappings).map(([field, value]) => (
                            <Grid item xs={12} sm={6} md={4} key={field}>
                                <TextField
                                    fullWidth
                                    label={t(`products.fields.${field}`)}
                                    value={value}
                                    onChange={(e) => handleColumnMapping(field, e.target.value)}
                                    placeholder="A1"
                                    helperText={t('products.import.columnFormat')}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            );
 
        case 2:
            return (
                <Box sx={{ width: '100%' }}>
                    <LinearProgress variant="determinate" value={progress} />
                    <Typography sx={{ mt: 1 }}>
                        {t('products.import.importing')} ({progress}%)
                    </Typography>
                </Box>
            );
 
        default:
            return null;
    }
 };

   return (
       <Dialog 
           open={open} 
           onClose={activeStep !== 2 ? onClose : undefined}
           maxWidth="md" 
           fullWidth
       >
           <DialogTitle>{t('products.import.title')}</DialogTitle>
           <DialogContent>
               {error && (
                   <Alert severity="error" sx={{ mb: 2 }}>
                       {error}
                   </Alert>
               )}
               <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                   {steps.map((label) => (
                       <Step key={label}>
                           <StepLabel>{label}</StepLabel>
                       </Step>
                   ))}
               </Stepper>
               {renderStep()}
           </DialogContent>
           <DialogActions>
               {activeStep !== 2 && (
                   <Button onClick={onClose}>
                       {t('common.cancel')}
                   </Button>
               )}
               {activeStep === 1 && (
                   <Button 
                       onClick={handleImport} 
                       variant="contained" 
                       color="primary"
                   >
                       {t('products.import.start')}
                   </Button>
               )}
           </DialogActions>
       </Dialog>
   );
};

export default ProductsImport;